/**
 * Códigos de erro estáveis.
 *
 * Em ambiente regulado a mensagem de erro é parte da usabilidade avaliada, e o
 * CÓDIGO é o que os testes de OQ verificam — a mensagem pode ser traduzida ou
 * reescrita sem invalidar a evidência de teste.
 */
export const ErrorCode = {
    // Autenticação / autorização
    INVALID_CREDENTIALS: 'AUTH.INVALID_CREDENTIALS',
    ACCOUNT_LOCKED: 'AUTH.ACCOUNT_LOCKED',
    ACCOUNT_INACTIVE: 'AUTH.ACCOUNT_INACTIVE',
    PASSWORD_EXPIRED: 'AUTH.PASSWORD_EXPIRED',
    MFA_REQUIRED: 'AUTH.MFA_REQUIRED',
    MFA_INVALID: 'AUTH.MFA_INVALID',
    SESSION_EXPIRED: 'AUTH.SESSION_EXPIRED',
    FORBIDDEN: 'AUTH.FORBIDDEN',
    // Política de senha — §11.300
    PASSWORD_TOO_SHORT: 'PASSWORD.TOO_SHORT',
    PASSWORD_MISSING_UPPERCASE: 'PASSWORD.MISSING_UPPERCASE',
    PASSWORD_MISSING_LOWERCASE: 'PASSWORD.MISSING_LOWERCASE',
    PASSWORD_MISSING_DIGIT: 'PASSWORD.MISSING_DIGIT',
    PASSWORD_MISSING_SYMBOL: 'PASSWORD.MISSING_SYMBOL',
    PASSWORD_REUSED: 'PASSWORD.REUSED',
    PASSWORD_TOO_COMMON: 'PASSWORD.TOO_COMMON',
    // Assinatura — Part 11 Subparte C
    SIGNATURE_PROOF_INVALID: 'SIGNATURE.PROOF_INVALID',
    SIGNATURE_PROOF_EXPIRED: 'SIGNATURE.PROOF_EXPIRED',
    SIGNATURE_PROOF_ALREADY_USED: 'SIGNATURE.PROOF_ALREADY_USED',
    SIGNATURE_PROOF_RECORD_MISMATCH: 'SIGNATURE.PROOF_RECORD_MISMATCH',
    SIGNATURE_IDENTITY_MISMATCH: 'SIGNATURE.IDENTITY_MISMATCH',
    SIGNATURE_REASON_REQUIRED: 'SIGNATURE.REASON_REQUIRED',
    SIGNATURE_NOT_AUTHORIZED_FOR_ACT: 'SIGNATURE.NOT_AUTHORIZED_FOR_ACT',
    SIGNATURE_RECORD_MUTATED: 'SIGNATURE.RECORD_MUTATED',
    // Tenancy
    CROSS_TENANT_ACCESS: 'TENANCY.CROSS_TENANT_ACCESS',
    ORGANIZATION_CONTEXT_MISSING: 'TENANCY.ORGANIZATION_CONTEXT_MISSING',
    // Documento
    DOCUMENT_NAME_DUPLICATE: 'DOCUMENT.NAME_DUPLICATE',
    DOCUMENT_NEW_FILE_REQUIRED: 'DOCUMENT.NEW_FILE_REQUIRED',
    DOCUMENT_VERSION_REGRESSION: 'DOCUMENT.VERSION_REGRESSION',
    DOCUMENT_VERSION_FORMAT: 'DOCUMENT.VERSION_FORMAT',
    DOCUMENT_ILLEGAL_TRANSITION: 'DOCUMENT.ILLEGAL_TRANSITION',
    DOCUMENT_NOT_APPROVER: 'DOCUMENT.NOT_APPROVER',
    DOCUMENT_ALREADY_DECIDED: 'DOCUMENT.ALREADY_DECIDED',
    DOCUMENT_SELF_APPROVAL: 'DOCUMENT.SELF_APPROVAL',
    DOCUMENT_APPROVAL_CONFIG_INVALID: 'DOCUMENT.APPROVAL_CONFIG_INVALID',
    DOCUMENT_OBSOLETE_DOWNLOAD_BLOCKED: 'DOCUMENT.OBSOLETE_DOWNLOAD_BLOCKED',
    DOCUMENT_TYPE_CYCLE: 'DOCUMENT.TYPE_CYCLE',
    DOCUMENT_TYPE_IN_USE: 'DOCUMENT.TYPE_IN_USE',
    /** Tipo com reunião ou solicitação vinculada — desative em vez de excluir. */
    MEETING_TYPE_IN_USE: 'MEETING.TYPE_IN_USE',
    REQUEST_TYPE_IN_USE: 'REQUEST.TYPE_IN_USE',
    /** Tipo semeado: código imutável e exclusão recusada. Desativar é permitido. */
    SGQ_TYPE_SYSTEM_IMMUTABLE: 'SGQ.TYPE_SYSTEM_IMMUTABLE',
    /** Tipo desativado não pode ser escolhido em registro novo. */
    SGQ_TYPE_INACTIVE: 'SGQ.TYPE_INACTIVE',
    /** O tipo da solicitação não admite abertura sem causa-raiz. */
    CAPA_STANDALONE_NOT_ALLOWED: 'CAPA.STANDALONE_NOT_ALLOWED',
    // Upload / mídia
    FILE_TOO_LARGE: 'MEDIA.FILE_TOO_LARGE',
    FILE_EXTENSION_NOT_ALLOWED: 'MEDIA.FILE_EXTENSION_NOT_ALLOWED',
    FILE_MIME_MISMATCH: 'MEDIA.FILE_MIME_MISMATCH',
    FILE_INFECTED: 'MEDIA.FILE_INFECTED',
    FILE_CHECKSUM_MISMATCH: 'MEDIA.FILE_CHECKSUM_MISMATCH',
    FILE_RETENTION_LOCKED: 'MEDIA.FILE_RETENTION_LOCKED',
    // NC / CAPA
    NC_ILLEGAL_TRANSITION: 'NC.ILLEGAL_TRANSITION',
    /** Encerramento barrado por ação pendente ou eficácia reprovada — o C16 que o legado nunca executava. */
    NC_CAPA_INCOMPLETE: 'NC.CAPA_INCOMPLETE',
    GUT_SCORE_INVALID: 'NC.GUT_SCORE_INVALID',
    NC_RECURRENCE_SELF: 'NC.RECURRENCE_SELF',
    CAPA_ILLEGAL_TRANSITION: 'CAPA.ILLEGAL_TRANSITION',
    CAPA_PLAN_INCOMPLETE: 'CAPA.PLAN_INCOMPLETE',
    CAPA_NO_ROOT_CAUSE: 'CAPA.NO_ROOT_CAUSE',
    CAPA_NOT_THE_VERIFIER: 'CAPA.NOT_THE_VERIFIER',
    CAPA_SELF_VERIFICATION: 'CAPA.SELF_VERIFICATION',
    CAPA_NOT_THE_ASSIGNEE: 'CAPA.NOT_THE_ASSIGNEE',
    CAPA_REASSIGN_NOT_ALLOWED: 'CAPA.REASSIGN_NOT_ALLOWED',
    // Reunião
    MEETING_ILLEGAL_TRANSITION: 'MEETING.ILLEGAL_TRANSITION',
    MEETING_NO_PARTICIPANTS: 'MEETING.NO_PARTICIPANTS',
    MEETING_NC_LINK_REQUIRED: 'MEETING.NC_LINK_REQUIRED',
    MEETING_NOT_PARTICIPANT: 'MEETING.NOT_PARTICIPANT',
    MEETING_EFFECTIVENESS_NOT_APPLICABLE: 'MEETING.EFFECTIVENESS_NOT_APPLICABLE',
    // Circular
    CIRCULAR_ILLEGAL_TRANSITION: 'CIRCULAR.ILLEGAL_TRANSITION',
    CIRCULAR_ALREADY_ACKNOWLEDGED: 'CIRCULAR.ALREADY_ACKNOWLEDGED',
    CIRCULAR_NOT_RECIPIENT: 'CIRCULAR.NOT_RECIPIENT',
    CIRCULAR_IMMUTABLE: 'CIRCULAR.IMMUTABLE',
    // Auditoria
    AUDIT_CHAIN_BROKEN: 'AUDIT.CHAIN_BROKEN',
    AUDIT_IMMUTABLE: 'AUDIT.IMMUTABLE',
    /**
     * A organização não contratou o módulo que governa esta rota. HTTP 402.
     *
     * Não é `FORBIDDEN`, e a diferença é operacional: quem lê 403 no suporte vai
     * procurar o papel do usuário e não vai achar nada errado; quem lê este
     * código abre o contrato, que é onde o problema está.
     *
     * O corpo do erro traz o módulo em `fieldErrors.module`, para a interface
     * poder dizer QUAL contratar em vez de "recurso indisponível".
     */
    MODULE_NOT_CONTRACTED: 'PLATFORM.MODULE_NOT_CONTRACTED',
    /**
     * A organização não tem servidor de e-mail próprio nem contratou o serviço de
     * envio do fornecedor.
     *
     * NÃO é falha: é a ausência declarada de um caminho de saída. Vale como
     * motivo da entrega por e-mail não ter acontecido, e existe separado de um
     * erro de conexão justamente para o suporte não ir procurar rede quando o que
     * falta é contrato.
     */
    EMAIL_NO_TRANSPORT: 'EMAIL.NO_TRANSPORT',
    // Genéricos
    VALIDATION_FAILED: 'COMMON.VALIDATION_FAILED',
    NOT_FOUND: 'COMMON.NOT_FOUND',
    CONFLICT: 'COMMON.CONFLICT',
    RATE_LIMITED: 'COMMON.RATE_LIMITED',
    INTERNAL: 'COMMON.INTERNAL',
};
/**
 * Regra de resposta para acesso cross-tenant: SEMPRE 404, nunca 403.
 * Um 403 confirmaria a existência do recurso em outra organização, o que já é
 * vazamento de informação. A suíte de isolamento (plano §4.4, camada 3) exige 404.
 */
export const CROSS_TENANT_HTTP_STATUS = 404;
//# sourceMappingURL=errors.js.map